"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { DESTINATIONS, QUESTIONS, type Answer } from "./quiz-data";
import { rankDestinations, selectPitchline, type AnswerMap } from "./scoring";
import { QuestionScreen } from "./QuestionScreen";
import { ResultCard } from "./ResultCard";
import { ShareRow } from "./ShareRow";
import { trackEvent } from "@/lib/analytics";
import { buildWorkSignupHref } from "@/lib/signup-next";

type Phase = "intro" | "question" | "results";

type SharedResult = { destId: string; pct: number };

type Props = {
  jobCounts: Record<string, number>;
};

const subscribeToStaticLocationSnapshot = () => () => {};
const getSharedResultSnapshot = (): SharedResult | null => {
  const params = new URLSearchParams(window.location.search);
  const d = params.get("d");
  const pct = Number(params.get("pct") ?? "0");
  return d && pct > 0 ? { destId: d, pct } : null;
};
const getNoSharedResultSnapshot = () => null;

function getVisibleQuestions(answers: AnswerMap) {
  return QUESTIONS.filter((q) => {
    if (q.showIf === "sun-only") {
      const seasonAnswer = answers["season"];
      return seasonAnswer?.seasonFilter === "sun";
    }
    return true;
  });
}

export function QuizClient({ jobCounts }: Props) {
  const [phase, setPhase] = useState<Phase>("intro");
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const startedRef = useRef(false);
  const sharedResult = useSyncExternalStore(
    subscribeToStaticLocationSnapshot,
    getSharedResultSnapshot,
    getNoSharedResultSnapshot
  );

  const sharedDest = sharedResult
    ? DESTINATIONS.find((dest) => dest.id === sharedResult.destId)
    : null;

  const visibleQuestions = useMemo(() => getVisibleQuestions(answers), [answers]);
  const currentQuestion = visibleQuestions[index];

  const ranked = useMemo(() => {
    if (phase !== "results") return [];
    return rankDestinations(DESTINATIONS, answers, QUESTIONS, jobCounts).slice(0, 3);
  }, [phase, answers, jobCounts]);

  useEffect(() => {
    if (phase === "question" && !startedRef.current) {
      trackEvent("destination_quiz_started");
      startedRef.current = true;
    }
  }, [phase]);

  useEffect(() => {
    if (phase === "results") {
      trackEvent("destination_quiz_completed");
    }
  }, [phase]);

  function selectAnswer(answerId: string) {
    const answer: Answer | undefined = currentQuestion.answers.find((a) => a.id === answerId);
    if (!answer) return;

    if (currentQuestion.id === "season" && answer.seasonFilter === "festival") {
      window.location.href = "/festivals/quiz";
      return;
    }

    const next: AnswerMap = { ...answers, [currentQuestion.id]: answer };
    setAnswers(next);

    const nextVisible = getVisibleQuestions(next);
    const nextIndex = index + 1;

    if (nextIndex < nextVisible.length) {
      setTimeout(() => setIndex(nextIndex), 200);
    } else {
      setTimeout(() => setPhase("results"), 200);
    }
  }

  function goBack() {
    if (index > 0) setIndex(index - 1);
  }

  function restart() {
    setAnswers({});
    setIndex(0);
    setPhase("intro");
  }

  if (phase === "intro") {
    return (
      <section className="px-6 md:px-10 pt-12 md:pt-20 pb-16 text-center min-h-[calc(100vh-80px)] flex items-center animate-fade-in">
        <div className="max-w-[620px] mx-auto">
          {sharedDest && sharedResult ? (
            <div className="mb-8 rounded-[16px] border border-gold/30 bg-gold/[0.04] px-6 py-5">
              <p className="text-[15px] md:text-[17px] text-navy font-medium">
                Your friend got {sharedResult.pct}% {sharedDest.name}. What&apos;s yours?
              </p>
            </div>
          ) : null}
          <p className="text-[11px] font-semibold uppercase tracking-[2px] text-gold mb-4">
            60 second quiz
          </p>
          <h1 className="font-serif text-[40px] md:text-[60px] leading-[1.05] tracking-[-0.5px] text-navy mb-5">
            Where should I do a season?
          </h1>
          <p className="text-[15px] md:text-[17px] leading-[1.6] text-text-muted mb-9 max-w-[480px] mx-auto">
            Quick questions, three destinations picked for you. Live job counts, guides, and the honest lowdown on what it&apos;s actually like.
          </p>
          <button
            type="button"
            onClick={() => setPhase("question")}
            className="bg-gold text-white px-8 py-3.5 rounded-sm text-sm font-semibold transition-all hover:bg-gold-light hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(28, 138, 153,0.3)]"
          >
            Start the quiz &rarr;
          </button>
        </div>
      </section>
    );
  }

  if (phase === "question") {
    return (
      <QuestionScreen
        key={currentQuestion.id}
        question={currentQuestion}
        total={visibleQuestions.length}
        index={index}
        selectedAnswerId={answers[currentQuestion.id]?.id}
        onSelect={selectAnswer}
        onBack={goBack}
      />
    );
  }

  return (
    <section className="px-6 md:px-10 pt-8 md:pt-12 pb-20 animate-fade-in">
      <div className="max-w-[880px] mx-auto">
        <div className="text-center mb-10">
          <p className="text-[11px] font-semibold uppercase tracking-[2px] text-gold mb-3">
            Your top matches
          </p>
          <h1 className="font-serif text-[34px] md:text-[48px] leading-[1.1] text-navy mb-3">
            You&apos;d fit right in.
          </h1>
          <p className="text-[14.5px] text-text-muted max-w-[520px] mx-auto">
            Based on what you told us, these three destinations line up best. Read the guides, check live conditions, or jump straight to open jobs.
          </p>
        </div>

        <div className="grid gap-5 md:gap-6">
          {ranked.map((r, i) => (
            <ResultCard
              key={r.destination.id}
              rank={i + 1}
              destination={r.destination}
              matchPercent={r.matchPercent}
              liveJobs={r.liveJobs}
              pitch={selectPitchline(r.destination, answers, QUESTIONS)}
            />
          ))}
        </div>

        {ranked[0] ? (
          <ShareRow destination={ranked[0].destination} matchPercent={ranked[0].matchPercent} />
        ) : null}

        <div className="mt-12 md:mt-14 bg-gradient-to-br from-gold/[0.06] to-gold/[0.02] border-[1.5px] border-gold/20 rounded-[20px] p-7 md:p-10 text-center">
          <h2 className="font-serif text-[24px] md:text-[30px] text-navy mb-2">
            Create your profile to get hired
          </h2>
          <p className="text-[14.5px] text-text-muted mb-5 max-w-[480px] mx-auto">
            Employers in these destinations browse PeakWave daily. Always free, never any fees.
          </p>
          <div className="flex flex-col md:flex-row gap-3 justify-center">
            <Link
              href={buildWorkSignupHref({ next: "/jobs", source: "plan:quiz" })}
              className="bg-gold text-white px-7 py-3 rounded-sm text-sm font-semibold no-underline transition-all hover:bg-gold-light"
            >
              Create Profile
            </Link>
            <button
              type="button"
              onClick={restart}
              className="bg-transparent text-charcoal px-7 py-3 rounded-sm text-sm font-semibold border-2 border-charcoal/15 transition-all hover:border-gold hover:text-gold"
            >
              Retake quiz
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
